import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { FilePlus2, ShieldCheck } from "lucide-react";
import { from, getClient, postgrestSource, rpc, translateError, useAction, useHasRole, useOrg } from "@vixio/data";
import { Button, Callout, DataTable, Dialog, KpiRow, KpiTile, PageHeader, Section, Select, StatusFromMap, TextArea, TextInput, formatInt, monthStartIso, todayIso } from "@vixio/ui";
import { DOC_STATUS, sha256Hex, useDocumentTypes, type IssuedDocument } from "./api";

async function countQ(q: PromiseLike<{ count: number | null; error: unknown }>): Promise<number> {
  const r = await q;
  if (r.error) throw translateError(r.error);
  return r.count ?? 0;
}

export function useDocKpis(orgId: string) {
  const month = monthStartIso(todayIso());
  return useQuery({
    queryKey: ["docs", "kpis", orgId, month],
    queryFn: async () => {
      const base = () => from("docs", "issued_documents").select("id", { count: "exact", head: true }).eq("org_id", orgId);
      const [valid, revoked, superseded, thisMonth] = await Promise.all([
        countQ(base().eq("status", "valid")),
        countQ(base().eq("status", "revoked")),
        countQ(base().eq("status", "superseded")),
        countQ(base().gte("issued_at", `${month}T00:00:00-03:00`)),
      ]);
      return { valid, revoked, superseded, thisMonth };
    },
  });
}

/**
 * Registro manual de documento emitido: o hash vem do arquivo (calculado no navegador) ou do conteúdo digitado.
 * Emissões de outros módulos (propostas, certificados) chegam sozinhas pela RPC docs.issue.
 */
export function IssueDialog({ open, onOpenChange, onIssued }: { open: boolean; onOpenChange: (o: boolean) => void; onIssued?: (r: { id: string; code: string }) => void }) {
  const org = useOrg();
  const navigate = useNavigate();
  const types = useDocumentTypes();
  const [type, setType] = useState("outro");
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [pubLine, setPubLine] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const invalid = title.trim().length < 2 || (!file && content.trim().length < 10);
  const issue = useAction(
    async () => {
      const sha = file ? await sha256Hex(await file.arrayBuffer()) : await sha256Hex(content.trim());
      const snapshot: Record<string, unknown> = {
        registro: file ? "arquivo" : "texto",
        ...(file ? { arquivo: { nome: file.name, tamanho: file.size, tipo: file.type } } : { conteudo: content.trim() }),
        public: { titulo: title.trim(), ...(pubLine.trim() ? { descricao: pubLine.trim() } : {}) },
      };
      const r = await rpc("docs", "issue", { p_org: org.id, p_type: type, p_title: title.trim(), p_origin_type: null, p_origin_id: null, p_sha256: sha, p_snapshot: snapshot });
      // PDF anexado: sobe para o bucket privado (<org>/<tipo>/<código>.pdf), registra o caminho e pede ao worker o
      // registro do hash do arquivo. Falha no envio não desfaz a emissão: o documento fica sem arquivo.
      let uploaded = false;
      if (r.created && file && (file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf"))) {
        const path = `${org.id}/${type}/${r.code}.pdf`;
        const up = await getClient().storage.from("docs-emitidos").upload(path, file, { contentType: "application/pdf", upsert: false });
        if (!up.error) {
          await rpc("docs", "attach_file", { p_id: r.id, p_storage_path: path });
          await rpc("docs", "request_render", { p_document: r.id });
          uploaded = true;
        }
      }
      return { ...r, uploaded };
    },
    {
      invalidate: [["docs"]],
      success: (r) => (r.created ? `Documento ${r.code} emitido${r.uploaded ? " com o PDF guardado" : ""}` : `Já existia: ${r.code}`),
      onSuccess: (r) => {
        onOpenChange(false);
        if (onIssued) onIssued(r);
        else navigate(`/documentos/emitidos/${r.id}`);
      },
    },
  );
  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (o) {
          setTitle("");
          setContent("");
          setFile(null);
          setPubLine("");
          setSubmitted(false);
        }
        onOpenChange(o);
      }}
      title="Emitir documento"
      description="Gera o código público AAAA-DOC-XXXXXXXX e guarda o SHA-256. Emitir de novo o mesmo conteúdo devolve o documento existente."
      onSubmit={() => {
        setSubmitted(true);
        if (!invalid) issue.mutate(undefined);
      }}
      footer={
        <>
          <Button onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button type="submit" variant="primary" loading={issue.isPending}>
            Emitir
          </Button>
        </>
      }
    >
      <div className="vx-stack">
        <div className="vx-form-grid">
          <Select label="Tipo" value={type} onChange={setType} options={(types.data ?? []).filter((t) => t.active).map((t) => ({ value: t.code, label: t.label }))} />
          <TextInput label="Título" value={title} onChange={setTitle} maxLength={300} error={submitted && title.trim().length < 2 ? "Informe o título." : null} />
        </div>
        <div className="vx-field">
          <label className="vx-field__label" htmlFor="vx-issue-file">
            Arquivo final (PDF)
          </label>
          <input id="vx-issue-file" type="file" accept="application/pdf,.pdf" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
          <span className="vx-field__hint">O hash é calculado aqui e o PDF fica guardado no armazenamento privado da organização. Sem arquivo, o hash é do texto abaixo e o PDF é gerado pelo sistema.</span>
        </div>
        {!file ? (
          <TextArea label="Conteúdo" value={content} onChange={setContent} rows={5} maxLength={100000} error={submitted && content.trim().length < 10 ? "Anexe o arquivo ou escreva o conteúdo." : null} />
        ) : null}
        <TextInput label="Descrição pública" value={pubLine} onChange={setPubLine} maxLength={300} hint="Aparece na verificação pública junto com o título. Nada de CPF, e-mail ou valores." />
      </div>
    </Dialog>
  );
}

export function DocumentsPage() {
  const org = useOrg();
  const navigate = useNavigate();
  const canWrite = useHasRole("member");
  const types = useDocumentTypes();
  const kpis = useDocKpis(org.id);
  const [issuing, setIssuing] = useState(false);
  const source = useMemo(() => postgrestSource<IssuedDocument>({ schema: "docs", table: "v_issued_documents", orgId: org.id, searchColumns: ["code", "title"], tiebreak: "id" }), [org.id]);
  return (
    <div className="vx-content">
      <PageHeader
        title="Documentos emitidos"
        description="O arquivo é representação; a linha no banco é a verdade: código público, SHA-256, conteúdo congelado e situação. Documento não se apaga: revoga ou substitui."
        actions={
          <>
            <Button icon={<ShieldCheck />} onClick={() => navigate("/documentos/verificar")}>
              Verificar
            </Button>
            {canWrite ? (
              <Button variant="primary" icon={<FilePlus2 />} onClick={() => setIssuing(true)}>
                Emitir documento
              </Button>
            ) : null}
          </>
        }
      />
      <KpiRow>
        <KpiTile label="Válidos" value={formatInt(kpis.data?.valid ?? 0)} />
        <KpiTile label="Emitidos no mês" value={formatInt(kpis.data?.thisMonth ?? 0)} />
        <KpiTile label="Substituídos" value={formatInt(kpis.data?.superseded ?? 0)} />
        <KpiTile label="Revogados" value={formatInt(kpis.data?.revoked ?? 0)} tone={(kpis.data?.revoked ?? 0) > 0 ? "warn" : undefined} />
      </KpiRow>
      <Section title="Documentos" id="documentos">
        <DataTable<IssuedDocument>
          caption="Documentos emitidos"
          source={source}
          rowKey={(d) => d.id}
          onRowClick={(d) => navigate(`/documentos/emitidos/${d.id}`)}
          initialSort={{ key: "issued_at", dir: "desc" }}
          searchPlaceholder="Buscar por código ou título"
          exportName="documentos-emitidos"
          empty="Nenhum documento emitido."
          filters={[
            { key: "status", label: "Situação", all: "Todas", options: Object.entries(DOC_STATUS).map(([value, s]) => ({ value, label: s.label })) },
            { key: "type", label: "Tipo", all: "Todos os tipos", options: (types.data ?? []).map((t) => ({ value: t.code, label: t.label })) },
          ]}
          columns={[
            { key: "code", header: "Código", sortable: true, cell: (d) => <Link to={`/documentos/emitidos/${d.id}`} className="mono" onClick={(e) => e.stopPropagation()}>{d.code}</Link> },
            { key: "title", header: "Título", sortable: true, cell: (d) => <span className="vx-table__primary">{d.title}<span className="vx-table__sub">{d.type_label}</span></span> },
            { key: "origin_type", header: "Origem", cell: (d) => d.origin_type ?? <span className="muted">manual</span>, csv: (d) => d.origin_type ?? "manual" },
            { key: "issued_at", header: "Emitido", kind: "datetime", sortable: true },
            { key: "storage_path", header: "PDF", cell: (d) => (d.storage_path ? "guardado" : <span className="muted">—</span>), csv: (d) => (d.storage_path ? "guardado" : "") },
            { key: "status", header: "Situação", cell: (d) => <StatusFromMap map={DOC_STATUS} value={d.status} />, csv: (d) => DOC_STATUS[d.status]?.label ?? d.status },
          ]}
        />
      </Section>
      <IssueDialog open={issuing} onOpenChange={setIssuing} />
    </div>
  );
}
